/**
 * DAG synchronization protocol
 *
 * Implements selective sync:
 * - Metadata objects sync eagerly to all peers
 * - Large content (clipboard chunks) sync on-demand only
 */

import type { DAGObject } from '../dag/types.js';
import type { DAGStore } from '../dag/store.js';
import { validateDAGObject } from '../validation/index.js';

/**
 * Object types that should NOT be eagerly synced.
 * These are fetched on-demand when content is accessed.
 */
const ON_DEMAND_TYPES = new Set([
  'clipboard.chunk',
  'clipboard.manifest',
]);

/**
 * Check if an object type should be synced eagerly
 */
export function shouldSyncEagerly(type: string): boolean {
  return !ON_DEMAND_TYPES.has(type);
}

export interface SyncMessage {
  type: 'have' | 'want' | 'data' | 'broadcast' | 'fetch' | 'content';
  hashes?: string[];  // For have/want/fetch
  objects?: DAGObject[];  // For data/broadcast/content
  onDemand?: boolean;  // For want - indicates this is an on-demand request
}

/**
 * Encode sync message
 */
export function encodeSyncMessage(msg: SyncMessage): string {
  return JSON.stringify(msg);
}

/**
 * Decode sync message
 */
export function decodeSyncMessage(data: string): SyncMessage {
  return JSON.parse(data);
}

/**
 * DAG synchronization manager
 */
export class DAGSync {
  private store: DAGStore;

  constructor(store: DAGStore) {
    this.store = store;
  }

  /**
   * Create a "have" message with hashes of metadata objects only.
   * Large content (chunks) are NOT included - they sync on-demand.
   */
  async createHaveMessage(): Promise<SyncMessage> {
    const all = await this.store.getAll();
    // Only include objects that should sync eagerly (not chunks/manifests)
    const hashes = all
      .filter(obj => shouldSyncEagerly(obj.type))
      .map(obj => obj.hash);
    return { type: 'have', hashes };
  }

  /**
   * Process incoming "have" message, return "want" for missing objects
   */
  async processHaveMessage(hashes: string[]): Promise<SyncMessage> {
    const missing: string[] = [];
    for (const hash of hashes) {
      const exists = await this.store.has(hash);
      if (!exists) {
        missing.push(hash);
      }
    }
    return { type: 'want', hashes: missing };
  }

  /**
   * Process incoming "want" message, return requested objects
   * Only sends objects that pass validation (prevents propagating corrupt data)
   */
  async processWantMessage(hashes: string[]): Promise<SyncMessage> {
    const objects: DAGObject[] = [];
    for (const hash of hashes) {
      const obj = await this.store.get(hash);
      if (obj) {
        // Validate before sending - don't propagate invalid objects
        const result = validateDAGObject(obj, { allowServerSigned: true });
        if (result.valid) {
          objects.push(obj);
        } else {
          console.warn(
            `[DAGSync] Not sending invalid object ${hash.slice(0, 8)}: ${result.errors.join(', ')}`
          );
        }
      }
    }
    return { type: 'data', objects };
  }

  /**
   * Process incoming "data" message, store new objects
   * Returns array of newly stored object hashes
   */
  async processDataMessage(objects: DAGObject[]): Promise<string[]> {
    const stored: string[] = [];
    for (const obj of objects) {
      const exists = await this.store.has(obj.hash);
      if (!exists) {
        // Validate object before storing (hash, signature, timestamp, pubkey)
        const result = validateDAGObject(obj, { allowServerSigned: true });
        if (!result.valid) {
          console.warn(
            `[DAGSync] Rejected invalid object ${obj.hash?.slice(0, 8)}: ${result.errors.join(', ')}`
          );
          continue;
        }
        await this.store.put(obj);
        stored.push(obj.hash);
      }
    }
    return stored;
  }

  /**
   * Create a broadcast message for a new object.
   * Only broadcasts metadata objects - chunks are NOT broadcast.
   */
  createBroadcastMessage(obj: DAGObject): SyncMessage | null {
    // Don't broadcast large content objects
    if (!shouldSyncEagerly(obj.type)) {
      return null;
    }
    return { type: 'broadcast', objects: [obj] };
  }

  /**
   * Create a fetch request for on-demand content (chunks/manifests).
   * Used when viewing clipboard content that isn't cached locally.
   */
  createFetchMessage(hashes: string[]): SyncMessage {
    return { type: 'fetch', hashes };
  }

  /**
   * Process incoming fetch request - return requested content objects.
   * Unlike 'want', this is specifically for on-demand content.
   */
  async processFetchMessage(hashes: string[]): Promise<SyncMessage> {
    const objects: DAGObject[] = [];
    for (const hash of hashes) {
      const obj = await this.store.get(hash);
      if (obj) {
        const result = validateDAGObject(obj, { allowServerSigned: true });
        if (result.valid) {
          objects.push(obj);
        }
      }
    }
    return { type: 'content', objects };
  }

  /**
   * Handle incoming sync message
   * Returns [response message or null, array of newly stored hashes]
   */
  async handleMessage(data: string): Promise<{ response: SyncMessage | null; stored: string[] }> {
    const msg = decodeSyncMessage(data);

    switch (msg.type) {
      case 'have':
        return { response: await this.processHaveMessage(msg.hashes ?? []), stored: [] };

      case 'want':
        return { response: await this.processWantMessage(msg.hashes ?? []), stored: [] };

      case 'data':
        const dataStored = await this.processDataMessage(msg.objects ?? []);
        return { response: null, stored: dataStored };

      case 'broadcast':
        const broadcastStored = await this.processDataMessage(msg.objects ?? []);
        return { response: null, stored: broadcastStored };

      case 'fetch':
        // On-demand content request
        return { response: await this.processFetchMessage(msg.hashes ?? []), stored: [] };

      case 'content':
        // On-demand content response - store it
        const contentStored = await this.processDataMessage(msg.objects ?? []);
        return { response: null, stored: contentStored };

      default:
        return { response: null, stored: [] };
    }
  }

  /**
   * Check if we have all chunks for a clipboard entry.
   * Returns list of missing chunk hashes.
   */
  async getMissingChunks(manifestHash: string): Promise<string[]> {
    const manifest = await this.store.get(manifestHash);
    if (!manifest || manifest.type !== 'clipboard.manifest') {
      // Don't have manifest - need it and all chunks
      return [manifestHash];
    }

    const content = manifest.content as { chunks: string[] };
    const missing: string[] = [];

    for (const chunkHash of content.chunks) {
      const exists = await this.store.has(chunkHash);
      if (!exists) {
        missing.push(chunkHash);
      }
    }

    return missing;
  }
}
